import { Lock, Check } from "lucide-react";
import useDirectCheckout from "@/hooks/useDirectCheckout";

const SYSTEM_IMAGE = "/pickle-wall-professional-system.png";
const COMPLETE_SYSTEM_IMAGE =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_pickle-trainer-v1/artifacts/1bxyqugt_1D945933-46C2-46BB-A63E-2B2993AF3386.png";

function SystemCard({ id, badge, highlight, title, price, image, imageFit = "cover", imageAlt, bullets, ctaLabel, onBuy, loading }) {
  return (
    <div
      data-testid={`home-system-card-${id}`}
      className={`relative rounded-3xl border overflow-hidden bg-[#111] flex flex-col ${
        highlight ? "border-[#FF6B00] orange-glow" : "border-white/10"
      }`}
    >
      {badge && (
        <div
          className={`absolute top-4 left-4 z-10 text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1.5 rounded-full ${
            highlight ? "bg-[#FF6B00] text-black" : "bg-white/10 text-white/70 border border-white/20"
          }`}
        >
          {badge}
        </div>
      )}
      <div className="aspect-[4/3] w-full overflow-hidden bg-[#0a0a0a]">
        <img
          src={image}
          alt={imageAlt}
          className={`w-full h-full ${imageFit === "contain" ? "object-contain" : "object-cover"}`}
          loading="lazy"
        />
      </div>
      <div className="p-6 md:p-8 flex flex-col flex-1">
        <h3 className="font-display font-black uppercase tracking-tight text-2xl leading-tight">{title}</h3>
        <div className="mt-2 font-display text-3xl font-black text-[#FF6B00]">${price}</div>
        <ul className="mt-4 space-y-1.5 text-sm text-white/75">
          {bullets.map((b) => (
            <li key={b} className="flex items-start gap-2">
              <Check size={14} className="text-[#FF6B00] mt-0.5 shrink-0" />
              {b}
            </li>
          ))}
        </ul>
        <button
          type="button"
          data-testid={`home-system-buy-${id}`}
          onClick={onBuy}
          disabled={loading}
          className="mt-6 w-full bg-[#FF6B00] hover:bg-[#E66000] disabled:opacity-50 text-black font-bold uppercase tracking-wider text-sm px-6 py-4 inline-flex items-center justify-center gap-2 transition-all hover:orange-glow"
        >
          <Lock size={14} />
          {loading ? "Redirecting to Checkout..." : ctaLabel}
        </button>
      </div>
    </div>
  );
}

export default function ChooseSystem() {
  const { loadingKey, buy } = useDirectCheckout();

  return (
    <section id="choose-system" className="relative py-24 md:py-36 bg-[#070707] border-y border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#FF6B00] mb-4">Pick Your System</div>
        <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tight text-balance">
          Choose Your Pickle Wall<span className="text-[#FF6B00]">™</span>
        </h2>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <SystemCard
            id="net-entry"
            badge="ENTRY SYSTEM"
            title="Pickle Wall™ Net Training System"
            price="249"
            image={SYSTEM_IMAGE}
            imageAlt="Pickle Wall™ training net and backpack, official product photography"
            bullets={[
              "Official 6 ft × 36 in Pickle Wall training net",
              "ATP ZONE training markings",
              "Heavy-duty mounting system",
              "Kitchen-line training markers",
            ]}
            ctaLabel="START TRAINING — $249"
            loading={loadingKey === "net-entry"}
            onBuy={() => buy({ key: "net-entry", variant: "NetEntry", productName: "Pickle Wall™ Net Training System", price: 249, category: "net" })}
          />
          <SystemCard
            id="complete-system"
            badge="COMPLETE SYSTEM"
            highlight
            title="Pickle Wall™ Complete Training System"
            price="369"
            image={COMPLETE_SYSTEM_IMAGE}
            imageFit="contain"
            imageAlt="Pickle Wall™ Complete Training System — net, PW700 paddles, PRO 48 pickleballs and Pro Tour backpack, official product photography"
            bullets={[
              "Everything in the Net Training System",
              "2 Pickle Wall™ PW700 paddles (carbon fiber, 16mm core)",
              "Pickle Wall™ PRO 48 pickleballs — 3-pack",
              "Pickle Wall™ Pro Tour backpack",
            ]}
            ctaLabel="GET THE COMPLETE SYSTEM — $369"
            loading={loadingKey === "complete-system"}
            onBuy={() => buy({ key: "complete-system", variant: "Standard", productName: "Pickle Wall™ Complete Training System", price: 369, category: "net" })}
          />
        </div>
      </div>
    </section>
  );
}
